import { useCallback, useEffect, useRef, useState } from 'react';
import { Flex } from '@servicetitan/anvil2';
import {
    PrequalificationDecision,
    Reviewing,
    ApplicationState,
    SecondLookState,
    DecisionDataStore,
} from '@servicetitan/financing-decision-shared';
import {
    PrequalificationStatus,
    PrequalsApi,
} from '@servicetitan/financing-api-client/src/prequals-api';
import {
    WorkflowStore,
    SetupDataStore,
    useProviderConfig,
    useSecondLookIntegration,
} from '@servicetitan/financing-workflow-shared';
import { useDependencies } from '@servicetitan/react-ioc';
import { useQuery } from '@tanstack/react-query';
import {
    SubmitApplicationDecision,
    type GetLoanApiResponse,
} from '@servicetitan/financing-api-client';
import {
    useSecondLookSubmission,
    useLoanStatusPolling,
    useLoanRefresh,
    useLoanSubmission,
} from '@servicetitan/financing-application-workflow-shared';
import { SelectedPlanStore } from '@servicetitan/financing-plans-shared';
import { usePrequalSubmission, usePrequalStatusPolling } from '../submission/hooks';

const PrequalificationReviewingWorkflowInner = () => {
    const { setPrequalDecisionData } =
        PrequalificationDecision.PrequalificationDecisionDataStore.useStore();
    const { decisionData, setDecisionData } = DecisionDataStore.useStore();
    const [applicationState, setApplicationState] = useState<ApplicationState>('sending');
    const [secondLookState, setSecondLookState] = useState<SecondLookState>('none');
    const [shouldFetchCalculations, setShouldFetchCalculations] = useState(false);
    const [secondLookDecision, setSecondLookDecision] = useState<SubmitApplicationDecision>(
        SubmitApplicationDecision.Submitted,
    );
    const { setWorkflowStage } = WorkflowStore.useStore();
    const { setupData } = SetupDataStore.useStore();
    const providerConfig = useProviderConfig();
    const secondLookIntegration = useSecondLookIntegration();
    const { setSelectedPlan } = SelectedPlanStore.useStore();
    const [prequalsApi] = useDependencies(PrequalsApi);

    const prequalId = setupData?.prequalificationId;

    const { useFinancingSubmission, hidePendingDecisionScreen } = providerConfig;
    const shouldUseFinancingApi = useFinancingSubmission === true;

    const { isSuccess: isFinancingSubmissionSuccess, loanId } = useLoanSubmission({
        prequalId,
        autoSubmit: shouldUseFinancingApi,
        enabled: shouldUseFinancingApi,
        onSuccess: () => {
            setApplicationState('reviewing');
        },
    });

    const { iovationComponent, isSuccess: isPrequalSubmissionSuccess } = usePrequalSubmission({
        prequalId: prequalId!,
        autoSubmit: !shouldUseFinancingApi,
        onSuccess() {
            setApplicationState('reviewing');
        },
    });

    const isSubmissionSuccess = shouldUseFinancingApi
        ? isFinancingSubmissionSuccess
        : isPrequalSubmissionSuccess;

    // FIX: useLoanRefresh should run for both financing API flows AND hard prequalification flows
    // For GreenSky, it needs to run when submitting loan applications after prequal approval
    // The hook internally checks useFinancingSubmission to determine which API to use
    useLoanRefresh({
        isSubmissionSuccess: shouldUseFinancingApi
            ? isFinancingSubmissionSuccess
            : isPrequalSubmissionSuccess,
        loanId: shouldUseFinancingApi ? (loanId ?? null) : null,
        prequalId: !shouldUseFinancingApi ? (prequalId ?? null) : null,
        isHardPrequalification: true,
    });

    // Second look submission hook - only submitted when main decision is declined
    const {
        submitSecondLook,
        loanId: secondLookLoanId,
        isBelowWaterfallThreshold,
        tenantSettingsLoaded,
    } = useSecondLookSubmission({
        autoSubmit: false,
        onSuccess: () => {
            setSecondLookState('reviewing');
        },
    });

    // Read through a ref so the polling callbacks below stay referentially stable: the polling hook
    // keys its decision effect and its ceiling timer off the callback identity.
    const decisionDataRef = useRef(decisionData);
    decisionDataRef.current = decisionData;

    const handleSecondLookDecisionChange = useCallback(
        (decision: SubmitApplicationDecision, loanData: GetLoanApiResponse) => {
            if (loanData.redirectUrl) {
                setSelectedPlan(null);
                setDecisionData({
                    ...decisionDataRef.current!,
                    decision: SubmitApplicationDecision.Submitted,
                    loanId: secondLookLoanId,
                    approvedAmount:
                        loanData.requestedAmount != null && loanData.approvedAmount != null
                            ? Math.min(loanData.requestedAmount, loanData.approvedAmount)
                            : (loanData.requestedAmount ?? loanData.approvedAmount ?? null),
                    maxCreditAmount: loanData.approvedAmount ?? null,
                    externalLoanId: loanData.externalId ?? '',
                    redirectUrl: loanData.redirectUrl,
                    declineRedirectUrl: loanData.declineRedirectUrl ?? null,
                });
                setWorkflowStage('second-look-decision');
            } else if (decision === SubmitApplicationDecision.Approved) {
                // Re-set shared stores
                setSelectedPlan(null);
                setDecisionData({
                    ...decisionDataRef.current!,
                    decision: SubmitApplicationDecision.Approved,
                    loanId: secondLookLoanId,
                    approvedAmount:
                        loanData.requestedAmount != null && loanData.approvedAmount != null
                            ? Math.min(loanData.requestedAmount, loanData.approvedAmount)
                            : (loanData.requestedAmount ?? loanData.approvedAmount ?? null),
                    maxCreditAmount: loanData.approvedAmount ?? null,
                    externalLoanId: loanData.externalId ?? '',
                });
                setWorkflowStage('second-look-decision');
            } else if (decision === SubmitApplicationDecision.Declined) {
                // Both lenders have now declined. Only the second-look decline details are merged
                // in, so the prequalification's own decision and ids stay intact.
                setDecisionData({
                    ...decisionDataRef.current!,
                    secondLookDeclined: true,
                    declineRedirectUrl: loanData.declineRedirectUrl ?? null,
                });
            }
            setSecondLookDecision(decision);
        },
        [secondLookLoanId, setSelectedPlan, setDecisionData, setWorkflowStage],
    );

    const handleSecondLookTimeout = useCallback(() => {
        setSecondLookDecision(SubmitApplicationDecision.Declined);
        setDecisionData({
            ...decisionDataRef.current!,
            secondLookDeclined: true,
        });
    }, [setDecisionData]);

    // Second look decision polling - when approved, transition to second-look-decision
    useLoanStatusPolling({
        loanId: secondLookLoanId,
        onDecisionChange: handleSecondLookDecisionChange,
        onTimeout: handleSecondLookTimeout,
    });

    // Poll for prequalification status - only start after submission succeeds
    const { prequalData } = usePrequalStatusPolling({
        prequalId: prequalId ?? null,
        enabled: isSubmissionSuccess && !shouldUseFinancingApi,
        onStatusChange: status => {
            if (status === PrequalificationStatus.Approved) {
                setApplicationState('approved');
                setShouldFetchCalculations(true);
            } else if (status === PrequalificationStatus.Declined) {
                setApplicationState('declined');
            } else if (status === PrequalificationStatus.Review) {
                setApplicationState('reviewing');
            }
        },
        onTimeout: () => {
            // On timeout, treat as under review
            setApplicationState('reviewing');

            if (hidePendingDecisionScreen) {
                return;
            }

            setPrequalDecisionData({
                externalPrequalId: prequalData?.externalPrequalId ?? null,
                status: PrequalificationStatus.Review,
                approvedPlans: undefined,
            });
            setWorkflowStage('decision');
        },
    });

    // Fetch calculations/approved plans for approved prequalifications
    const { data: calculationsData } = useQuery({
        queryKey: ['prequalCalculations', prequalId],
        queryFn: async () => {
            if (!prequalId) {
                return null;
            }
            const response = await prequalsApi.prequalificationsGetPrequalificationCalculations({
                prequalificationId: prequalId,
            });
            return response.data;
        },
        enabled: shouldFetchCalculations && !!prequalId,
        retry: false,
    });

    // Handle prequalification status changes
    useEffect(() => {
        if (!prequalData?.status) {
            return;
        }

        if (prequalData.status === PrequalificationStatus.Approved) {
            setApplicationState('approved');
        } else if (prequalData.status === PrequalificationStatus.Declined) {
            setApplicationState('declined');

            if (
                secondLookIntegration.isEnabled &&
                tenantSettingsLoaded &&
                !isBelowWaterfallThreshold
            ) {
                setSecondLookState('sending');
                submitSecondLook();
            }
        } else if (prequalData.status === PrequalificationStatus.Review) {
            setApplicationState('reviewing');
        }
    }, [
        prequalData?.status,
        secondLookIntegration.isEnabled,
        tenantSettingsLoaded,
        isBelowWaterfallThreshold,
        submitSecondLook,
    ]);

    // Handle transition to decision workflow for approved prequalifications
    useEffect(() => {
        if (
            prequalData?.status === PrequalificationStatus.Approved &&
            calculationsData &&
            applicationState === 'approved'
        ) {
            setPrequalDecisionData({
                externalPrequalId: prequalData.externalPrequalId ?? null,
                status: prequalData.status,
                approvedPlans: calculationsData,
                approvedAmount: prequalData.approvedAmount ?? null,
            });

            // Transition to decision stage
            setWorkflowStage('decision');
        }
    }, [prequalData, calculationsData, applicationState, setPrequalDecisionData, setWorkflowStage]);

    // Handle transition to decision workflow for declined prequalifications (when second look is not enabled or also declined)
    useEffect(() => {
        if (prequalData?.status !== PrequalificationStatus.Declined) {
            return;
        }

        const secondLookTransientDecisions = [
            SubmitApplicationDecision.Approved,
            SubmitApplicationDecision.Review,
            SubmitApplicationDecision.Submitted,
        ];

        // Only transition to declined decision if:
        // 1. Second look is not enabled for this business unit, OR
        // 2. Loan amount is below the waterfall threshold, OR
        // 3. Second-look decision is final and not approved/review/submitted
        if (
            !secondLookIntegration.isEnabled ||
            isBelowWaterfallThreshold ||
            (!secondLookTransientDecisions.includes(secondLookDecision) &&
                secondLookState !== 'none')
        ) {
            setPrequalDecisionData({
                externalPrequalId: prequalData.externalPrequalId ?? null,
                status: prequalData.status,
                approvedPlans: undefined,
            });

            // Transition to decision stage
            setWorkflowStage('decision');
        }
    }, [
        prequalData,
        secondLookIntegration.isEnabled,
        isBelowWaterfallThreshold,
        secondLookDecision,
        secondLookState,
        setPrequalDecisionData,
        setWorkflowStage,
    ]);

    // Handle transition for review status
    useEffect(() => {
        if (
            prequalData?.status === PrequalificationStatus.Review &&
            applicationState === 'reviewing'
        ) {
            if (hidePendingDecisionScreen) {
                return;
            }

            setPrequalDecisionData({
                externalPrequalId: prequalData.externalPrequalId ?? null,
                status: prequalData.status,
                approvedPlans: undefined,
            });

            // Transition to decision stage
            setWorkflowStage('decision');
        }
    }, [
        prequalData,
        applicationState,
        setPrequalDecisionData,
        setWorkflowStage,
        hidePendingDecisionScreen,
    ]);

    // Handle transition to decision workflow when useLoanRefresh updates decisionData
    // This is needed for GoodLeap which uses financing API (useLoanRefresh) instead of prequal polling
    useEffect(() => {
        // Only handle this for financing API flows (GoodLeap)
        if (!shouldUseFinancingApi) {
            return;
        }

        // Don't transition if decision is still Submitted (waiting for decision)
        if (
            !decisionData?.decision ||
            decisionData.decision === SubmitApplicationDecision.Submitted
        ) {
            return;
        }

        // Don't transition if second look is enabled and we're waiting for its decision
        // (but skip this wait if the amount is below the waterfall threshold)
        if (
            secondLookIntegration.isEnabled &&
            !isBelowWaterfallThreshold &&
            decisionData.decision === SubmitApplicationDecision.Declined
        ) {
            const secondLookTransientDecisions = [
                SubmitApplicationDecision.Approved,
                SubmitApplicationDecision.Review,
                SubmitApplicationDecision.Submitted,
            ];
            // Wait for the second-look decision if it's still in progress
            if (
                secondLookTransientDecisions.includes(secondLookDecision) &&
                secondLookState !== 'none'
            ) {
                return;
            }
        }

        // Update application state based on decision
        if (decisionData.decision === SubmitApplicationDecision.Approved) {
            setApplicationState('approved');
        } else if (decisionData.decision === SubmitApplicationDecision.Declined) {
            setApplicationState('declined');
        } else if (decisionData.decision === SubmitApplicationDecision.Review) {
            setApplicationState('reviewing');
            // If hidePendingDecisionScreen is true, stay on reviewing screen instead of transitioning to decision stage
            if (hidePendingDecisionScreen) {
                return;
            }
        }

        // Transition to decision stage
        // WorkflowRenderer will use decisionData directly for GoodLeap (doesn't need prequalDecisionData)
        setWorkflowStage('decision');
    }, [
        shouldUseFinancingApi,
        decisionData?.decision,
        secondLookIntegration.isEnabled,
        isBelowWaterfallThreshold,
        secondLookDecision,
        secondLookState,
        setWorkflowStage,
        hidePendingDecisionScreen,
    ]);

    return (
        <Flex
            direction="column"
            gap="6"
            alignItems="center"
            style={{ width: '100%', margin: '0 auto' }}
        >
            {iovationComponent}
            <div style={{ flexGrow: 1, width: '100%' }}>
                <Reviewing applicationState={applicationState} secondLookState={secondLookState} />
            </div>
        </Flex>
    );
};

export const PrequalificationReviewingWorkflow = () => <PrequalificationReviewingWorkflowInner />;
